/**
 * Soma de valores de venda (faturado, devolução, líquido) e contagem de notas
 * e de clientes diferentes.
 *
 * Lê: nada.
 * Usado por: dominio/painel.ts e importacao/resumo.ts.
 */
import type { LinhaVenda } from './tipos.ts';

export interface Totais {
  /** Quantidade de notas fiscais diferentes. */
  notas: number;
  /** Clientes diferentes que compraram (líquido do cliente no período maior que zero: devolução total não conta). */
  clientes: number;
  faturado: number;
  devolucao: number;
  liquido: number;
}

export const TOTAIS_ZERO: Totais = { notas: 0, clientes: 0, faturado: 0, devolucao: 0, liquido: 0 };

/** Vai somando vendas; conta cada nota fiscal e cada cliente uma vez só. */
export class Acumulador {
  faturado = 0;
  devolucao = 0;
  liquido = 0;
  readonly nfs = new Set<string>();
  /** Líquido de cada cliente (código do cliente → soma). */
  readonly liquidoPorCliente = new Map<string, number>();

  somar(venda: LinhaVenda): void {
    this.faturado += venda.valorFaturado;
    this.devolucao += venda.valorDevolucao;
    this.liquido += venda.valorLiquido;
    this.nfs.add(venda.nf);
    if (venda.codigoCliente !== '') {
      this.liquidoPorCliente.set(venda.codigoCliente, (this.liquidoPorCliente.get(venda.codigoCliente) ?? 0) + venda.valorLiquido);
    }
  }

  totais(): Totais {
    // Cliente que só teve nota devolvida inteira (líquido zero) não comprou de verdade.
    const clientes = [...this.liquidoPorCliente.values()].filter((valor) => valor > 0.005).length;
    return { notas: this.nfs.size, clientes, faturado: this.faturado, devolucao: this.devolucao, liquido: this.liquido };
  }
}

/** Soma notas e clientes como números (um cliente atendido por dois representantes conta duas vezes). */
export function somarTotais(lista: Totais[]): Totais {
  return lista.reduce(
    (soma, t) => ({
      notas: soma.notas + t.notas,
      clientes: soma.clientes + t.clientes,
      faturado: soma.faturado + t.faturado,
      devolucao: soma.devolucao + t.devolucao,
      liquido: soma.liquido + t.liquido,
    }),
    TOTAIS_ZERO,
  );
}
